/**
 * @fileoverview Task Progression Logic
 * Handles automatic task progression and completion logic
 * Updated to use repository pattern for direct percentage calculation
 */

import { 
  activateNextTaskAPI, 
  updateProgressAPI 
} from '../api/taskQueue';
import { LogPlanCentroCostoRepository } from '../repositories/LogPlanCentroCostoRepository';
import { TareaOrdenRepository } from '../repositories/TareaOrdenRepository';
import { pb } from '../helpers/pocketbase_helper';

/**
 * Calculate task progress using log percentage directly (NEW IMPLEMENTATION)
 * @param {string} tareaOrdenId - The ID of the tarea_orden record
 * @param {string} completedLogId - The ID of the completed log record
 * @returns {Promise<number>} New progress percentage (0-100)
 */
export const calculateTaskProgress = (arg1, arg2) => {
  // Overload 1: (tasks:Array, plan:Object) => number (SYNCHRONOUS)
  if (Array.isArray(arg1)) {
    const tasks = arg1;
    const plan = arg2 || {};
    if (!tasks || tasks.length === 0) return 0;
    if (!plan || typeof plan.peso !== 'number') return 0;
    const totalWeight = plan.peso;
    const completedWeight = tasks
      .filter(task => task.fin || task.fecha_fin)
      .reduce((sum, task) => sum + (task.peso || 0), 0);
    return Math.round((completedWeight / totalWeight) * 100);
  }

  // For non-array inputs, call the async version
  return calculateTaskProgressAsync(arg1, arg2);
};

/**
 * Calculate task progress using log percentage directly (ASYNC VERSION)
 * @param {string} tareaOrdenId - The ID of the tarea_orden record
 * @param {string} completedLogId - The ID of the completed log record
 * @returns {Promise<number>} New progress percentage (0-100)
 */
export const calculateTaskProgressAsync = async (tareaOrdenId, completedLogId) => {
  try {
    if (!tareaOrdenId) {
      throw new Error('Tarea Orden ID is required');
    }

    if (!completedLogId) {
      throw new Error('Completed Log ID is required');
    }

    const logRepository = new LogPlanCentroCostoRepository();
    const tareaRepository = new TareaOrdenRepository();

    const tareaOrden = await tareaRepository.getTaskOrder(tareaOrdenId);
    const currentPercentage = tareaOrden.porcentaje || 0;
    const logPercentage = await logRepository.getLogPercentage(completedLogId);
    const newPercentage = currentPercentage + logPercentage;

    const clampedPercentage = Math.max(0, Math.min(100, newPercentage));
    const updatedTareaOrden = await tareaRepository.updateTaskOrderPercentage(
      tareaOrdenId,
      clampedPercentage
    );

    // Prefer repository's returned porcentaje; fallback to clamped percentage
    return updatedTareaOrden?.porcentaje ?? Math.round(clampedPercentage);
  } catch (error) {
    console.error('Error calculating task progress:', error);
    throw new Error(`Failed to calculate task progress: ${error.message}`);
  }
};

/**
 * Activate next task in plan sequence using repository pattern
 * @param {string} ordenId - Work order ID
 * @param {number} currentOrdenPlan - Current task's orden_plan
 * @returns {Promise<Object|null>} Next activated task or null
 */
export const activateNextTaskInPlan = async (ordenId, currentOrdenPlan) => {
  if (!ordenId) {
    throw new Error('Order ID is required');
  }

  if (typeof currentOrdenPlan !== 'number') {
    throw new Error('Current orden_plan must be a number');
  }

  try {
    const logRepository = new LogPlanCentroCostoRepository();

    // Find next task in plan sequence - search for any task with orden_plan > currentOrdenPlan
    const nextTask = await logRepository.findNextTaskInPlan(ordenId, currentOrdenPlan);
    
    if (!nextTask) {
      return null;
    }

    // Activate the next task
    const activatedTask = await logRepository.activateTask(nextTask.id);
    
    return activatedTask;
  } catch (error) {
    console.error('Error activating next task in plan:', error);
    throw new Error(`Failed to activate next task: ${error.message}`);
  }
};

/**
 * Promote next ready task to priority 1 (for tasks already ready but with higher priority)
 * @param {string} operarioId - Operator ID
 * @returns {Promise<Object|null>} Next promoted task or null
 */
export const promoteNextReadyTaskToPriority1 = async (operarioId) => {
  if (!operarioId) {
    throw new Error('Operator ID is required');
  }

  try {
    const logRepository = new LogPlanCentroCostoRepository();

    // Find the next ready task with priority > 1 for this operator
    const nextReadyTask = await logRepository.getList(1, 1, {
      filter: `operario = "${operarioId}" && ready = true && prioridad > 1 && (fin = "" || fin = null)`,
      sort: 'prioridad'
    });
    
    if (!nextReadyTask.items.length) {
      return null;
    }

    // Promote the task to priority 1
    const promotedTask = await logRepository.promoteTaskToPriority1(nextReadyTask.items[0].id);
    
    return promotedTask;
  } catch (error) {
    console.error('Error promoting next ready task:', error);
    throw new Error(`Failed to promote next ready task: ${error.message}`);
  }
};

/**
 * Finalize task order when all tasks are completed
 * @param {string} tareaOrdenId - The ID of the tarea_orden record
 * @returns {Promise<Object>} Finalized task order
 */
export const finalizeTaskOrder = async (tareaOrdenId) => {
  if (!tareaOrdenId) {
    throw new Error('Tarea Orden ID is required');
  }

  try {
    const tareaRepository = new TareaOrdenRepository();

    // Mark task order as completed
    const completedTaskOrder = await tareaRepository.completeTaskOrder(tareaOrdenId);
    
    return completedTaskOrder;
  } catch (error) {
    console.error('Error finalizing task order:', error);
    throw new Error(`Failed to finalize task order: ${error.message}`);
  }
};

/**
 * Get the user from the last completed task's centro de costo
 * @param {string} ordenId - Work order ID
 * @param {number} completedOrdenPlan - Completed task's orden_plan
 * @returns {Promise<string|null>} User ID or null if not found
 */
export const getLastCentroCostoUser = async (ordenId, completedOrdenPlan) => {
  try {
    const logRepository = new LogPlanCentroCostoRepository();
    
    // Get the completed task to find its centro de costo
    const completedTask = await logRepository.findFirst(`orden = "${ordenId}" && orden_plan = ${completedOrdenPlan} && (fin != "" && fin != null)`);
    
    if (!completedTask || !completedTask.centro_costo) {
      return null;
    }

    // Get the centro de costo with its operario and proceso_proveedor
    const centroCosto = await pb.collection('centro_costo').getOne(completedTask.centro_costo, {
      expand: 'operario,proceso_proveedor'
    });

    // Priority: operario (user) > proceso_proveedor (proveedor)
    if (centroCosto.expand?.operario?.id) {
      return {
        type: 'usuario',
        id: centroCosto.expand.operario.id
      };
    } else if (centroCosto.expand?.proceso_proveedor?.id) {
      return {
        type: 'proveedor',
        id: centroCosto.expand.proceso_proveedor.id
      };
    }

    return null;
  } catch (error) {
    console.error('Error getting last centro costo user:', error);
    return null;
  }
};

/**
 * Create log_estado record for order finalization
 * @param {string} ordenId - Work order ID
 * @param {Object} userInfo - User info object with type and id
 * @returns {Promise<string>} Log estado ID
 */
export const createLogEstadoForFinalization = async (ordenId, userInfo) => {
  try {
    const now = new Date().toISOString();
    
    const logEstadoData = {
      fecha_estado: now
    };

    // Add the correct field based on user type
    if (userInfo.type === 'usuario') {
      logEstadoData.usuario = userInfo.id;
    } else if (userInfo.type === 'proveedor') {
      logEstadoData.proveedor = userInfo.id;
    } else {
      throw new Error(`Invalid user type: ${userInfo.type}`);
    }

    const logEstado = await pb.collection('log_estado').create(logEstadoData);
    return logEstado.id;
  } catch (error) {
    console.error('Error creating log_estado for finalization:', error);
    throw new Error(`Failed to create log_estado: ${error.message}`);
  }
};

/**
 * Finalize orden_produccion by updating finalizada field
 * @param {string} ordenId - Work order ID
 * @param {string} logEstadoId - Log estado ID
 * @returns {Promise<Object>} Updated orden_produccion
 */
export const finalizeOrdenProduccion = async (ordenId, logEstadoId) => {
  try {
    const updatedOrden = await pb.collection('orden_produccion').update(ordenId, {
      finalizada: logEstadoId
    });
    return updatedOrden;
  } catch (error) {
    console.error('Error finalizing orden_produccion:', error);
    throw new Error(`Failed to finalize orden_produccion: ${error.message}`);
  }
};

/**
 * Process complete task progression workflow
 * Handles: calculate progress -> activate next task -> finalize if last task
 * @param {string} tareaOrdenId - The ID of the tarea_orden record
 * @param {string} completedLogId - The ID of the completed log record
 * @param {string} ordenId - Work order ID
 * @param {number} completedOrdenPlan - Completed task's orden_plan
 * @returns {Promise<Object>} Result of the complete workflow
 */
export const processCompleteTaskProgression = async (tareaOrdenId, completedLogId, ordenId, completedOrdenPlan) => {
  if (!tareaOrdenId) {
    throw new Error('Tarea Orden ID is required');
  }

  if (!completedLogId) {
    throw new Error('Completed Log ID is required');
  }

  if (!ordenId) {
    throw new Error('Order ID is required');
  }

  if (typeof completedOrdenPlan !== 'number') {
    throw new Error('Completed orden_plan must be a number');
  }

  try {
    const results = {
      progressUpdated: false,
      nextTaskActivated: false,
      orderFinalized: false,
      finalPercentage: 0,
      nextTask: null,
      completedTaskOrder: null,
      logEstadoCreated: false,
      ordenProduccionFinalized: false
    };

    // Step 1: Try to activate the next task FIRST (before calculating progress)
    try {
      const nextTask = await activateNextTaskInPlan(ordenId, completedOrdenPlan);
      
      if (nextTask) {
        results.nextTaskActivated = true;
        results.nextTask = nextTask;
      }
    } catch (error) {
      console.warn('Could not activate next task:', error.message);
      // Don't throw error, just log warning
    }
    
    // Step 1.5: If no next task was activated in the same order, 
    // try to promote the next ready task to priority 1
    if (!results.nextTaskActivated) {
      try {
        // Get the operario from the completed task to find their next ready task
        const completedTask = await pb.collection('log_plan_centro_costo').getOne(completedLogId);
        if (completedTask.operario) {
          const promotedTask = await promoteNextReadyTaskToPriority1(completedTask.operario);
          if (promotedTask) {
            results.nextTaskActivated = true;
            results.nextTask = promotedTask;
          }
        }
      } catch (error) {
        console.warn('Could not promote next ready task:', error.message);
        // Don't throw error, just log warning
      }
    }
    
    // If no next task was activated, set to false
    if (!results.nextTaskActivated) {
      results.nextTaskActivated = false;
      results.nextTask = null;
    }

    // Step 2: Always calculate and update progress for the tarea_orden
    const finalPercentage = await calculateTaskProgressAsync(tareaOrdenId, completedLogId);
    results.progressUpdated = true;
    results.finalPercentage = finalPercentage || 0;

    // Step 3: If no next task found and progress is 100%, finalize the order
    if (!results.nextTaskActivated && results.finalPercentage >= 100) {
      try {
        // Get the user from the last completed task's centro de costo
        const userInfo = await getLastCentroCostoUser(ordenId, completedOrdenPlan);
        
        if (userInfo) {
          // Create log_estado record
          const logEstadoId = await createLogEstadoForFinalization(ordenId, userInfo);
          results.logEstadoCreated = true;
          
          // Update orden_produccion finalizada field
          const updatedOrden = await finalizeOrdenProduccion(ordenId, logEstadoId);
          results.ordenProduccionFinalized = true;
        } else {
          console.warn('Could not find user for order finalization:', ordenId);
        }

        // Also finalize the task order
        const completedTaskOrder = await finalizeTaskOrder(tareaOrdenId);
        results.orderFinalized = true;
        results.completedTaskOrder = completedTaskOrder;
      } catch (error) {
        console.error('Error during order finalization:', error);
        console.warn('Could not finalize task order:', error.message);
        // Don't throw error, just log warning
      }
    }

    return results;
  } catch (error) {
    console.error('Error in complete task progression:', error);
    throw new Error(`Failed to process complete task progression: ${error.message}`);
  }
};

/**
 * Find the next task to activate based on current priority
 * @param {Array} tasks - Array of task objects
 * @param {number} currentPriority - Current completed task priority
 * @returns {Object|null} Next task to activate or null
 */
export const findNextTask = (tasks, currentPriority) => {
  if (!tasks || !Array.isArray(tasks)) {
    return null;
  }

  const nextPriority = currentPriority + 1;
  return tasks.find(task => 
    task.prioridad === nextPriority && 
    !(task.fin || task.fecha_fin) && 
    !(task.inicio || task.fecha_inicio) &&
    !task.ready
  ) || null;
};

/**
 * Check if all tasks for an order are completed
 * @param {Array} tasks - Array of task objects
 * @returns {boolean} True if all tasks are completed
 */
export const areAllTasksCompleted = (tasks) => {
  if (!tasks || !Array.isArray(tasks) || tasks.length === 0) {
    return false;
  }

  return tasks.every(task => (task.fin && task.fin !== '') || (task.fecha_fin && task.fecha_fin !== ''));
};

/**
 * Activate the next priority task for an order
 * @param {Array} tasks - Array of task objects
 * @param {number} currentPriority - Current completed task priority
 * @param {string} ordenId - Order ID
 * @param {Function} activateNextTaskAPI - API function to activate next task
 * @returns {Promise<Object>} Result of activation
 */
export const activateNextTask = async (tasks, currentPriority, ordenId, activateNextTaskAPI) => {
  try {
    if (!tasks || !Array.isArray(tasks)) {
      throw new Error('Tasks array is required');
    }

    if (typeof currentPriority !== 'number') {
      throw new Error('Current priority must be a number');
    }

    if (!ordenId) {
      throw new Error('Order ID is required');
    }

    // Check if there's a next task to activate
    const nextTask = findNextTask(tasks, currentPriority);
    
    if (!nextTask) {
      return {
        success: true,
        nextTask: null,
        message: 'No more tasks to activate'
      };
    }

    // Activate the next task via API
    const result = await activateNextTaskAPI(ordenId, currentPriority);
    
    return result;
  } catch (error) {
    console.error('Error activating next task:', error);
    throw new Error(`Failed to activate next task: ${error.message}`);
  }
};

/**
 * Update task progress for an order
 * @param {string} ordenId - Order ID
 * @param {number} progress - Progress percentage (0-100)
 * @param {Function} updateProgressAPI - API function to update progress
 * @returns {Promise<Object>} Result of progress update
 */
export const updateTaskProgress = async (ordenId, progress, updateProgressAPI) => {
  try {
    if (!ordenId) {
      throw new Error('Order ID is required');
    }

    if (typeof progress !== 'number' || progress < 0 || progress > 100) {
      throw new Error('Progress must be a number between 0 and 100');
    }

    const result = await updateProgressAPI(ordenId, progress);
    
    return result;
  } catch (error) {
    console.error('Error updating task progress:', error);
    throw new Error(`Failed to update task progress: ${error.message}`);
  }
};

/**
 * Complete a task order if all tasks are finished
 * @param {string} tareaOrdenId - The ID of the tarea_orden record
 * @param {string} completedLogId - The ID of the completed log record
 * @param {Function} updateProgressAPI - API function to update progress
 * @returns {Promise<Object>} Result of completion
 */
export const completeTaskOrder = async (tareaOrdenId, completedLogId, updateProgressAPI) => {
  try {
    if (!tareaOrdenId) {
      throw new Error('Tarea Orden ID is required');
    }

    if (!completedLogId) {
      throw new Error('Completed Log ID is required');
    }

    // Calculate progress using the new logic
    const updatedTareaOrden = await calculateTaskProgressAsync(tareaOrdenId, completedLogId);
    
    // Check if progress reached 100%
    const isCompleted = (updatedTareaOrden.porcentaje || 0) >= 100;
    
    const result = await updateProgressAPI(tareaOrdenId, updatedTareaOrden.porcentaje || 0);
    
    return {
      ...result,
      completed: isCompleted,
      tareaOrden: updatedTareaOrden
    };
  } catch (error) {
    console.error('Error completing task order:', error);
    throw new Error(`Failed to complete task order: ${error.message}`);
  }
};

/**
 * Process task completion and handle progression
 * @param {string} taskId - Completed task ID
 * @param {string} ordenId - Order ID
 * @param {Array} tasks - Array of task objects
 * @param {Object} completedTask - The completed task object
 * @param {Function} updateProgressAPI - API function to update progress
 * @param {Function} activateNextTaskAPI - API function to activate next task
 * @param {string} tareaOrdenId - The ID of the tarea_orden record
 * @returns {Promise<Object>} Result of processing
 */
export const processTaskCompletion = async (taskId, ordenId, tasks, completedTask, updateProgressAPI, activateNextTaskAPI, tareaOrdenId, completedTaskOrdenPlan) => {
  try {
    console.log('🚀 PROCESS TASK COMPLETION - Starting with:', {
      taskId,
      ordenId,
      tasksCount: tasks?.length,
      completedTaskId: completedTask?.id,
      tareaOrdenId,
      completedTaskOrdenPlan,
      completedTaskOrdenPlanType: typeof completedTaskOrdenPlan
    });

    if (!taskId) {
      throw new Error('Task ID is required');
    }

    if (!ordenId) {
      throw new Error('Order ID is required');
    }

    if (!tasks || !Array.isArray(tasks)) {
      throw new Error('Tasks array is required');
    }

    if (!completedTask) {
      throw new Error('Completed task object is required');
    }

    if (!tareaOrdenId) {
      throw new Error('Tarea Orden ID is required');
    }

    // Use provided completedTaskOrdenPlan or try to get it from completedTask
    const ordenPlan = completedTaskOrdenPlan || completedTask.orden_plan;
    console.log('🔍 Resolved ordenPlan:', ordenPlan, 'from completedTaskOrdenPlan:', completedTaskOrdenPlan, 'or completedTask.orden_plan:', completedTask.orden_plan);
    
    if (!ordenPlan) {
      throw new Error('Completed task orden_plan is required');
    }

    const results = {
      taskCompleted: true,
      nextTaskActivated: false,
      orderCompleted: false,
      progressUpdated: false
    };

    // Find the log ID for the completed task
    // The completedTask should have a log_plan field that references the log
    const completedLogId = completedTask.log_plan || completedTask.id;
    
    if (!completedLogId) {
      throw new Error('Completed task must have a log_plan or id field');
    }

    // Use the new completeTaskProgression function
    // Pass the completed task's orden_plan to activate the next task in sequence
    // Use the resolved ordenPlan instead of completedTask.orden_plan
    const completedOrdenPlan = ordenPlan; // Use the already resolved ordenPlan
    console.log('🔍 Using completedOrdenPlan (resolved):', completedOrdenPlan, 'type:', typeof completedOrdenPlan);
    
    if (typeof completedOrdenPlan !== 'number') {
      throw new Error('Completed task must have a valid orden_plan');
    }
    
    console.log('🔄 Calling processCompleteTaskProgression with:', {
      tareaOrdenId,
      completedLogId,
      ordenId,
      completedOrdenPlan
    });
    
    const progressionResult = await processCompleteTaskProgression(
      tareaOrdenId,
      completedLogId,
      ordenId,
      completedOrdenPlan
    );

    console.log('✅ processCompleteTaskProgression result:', progressionResult);

    results.progressUpdated = true;
    results.orderCompleted = progressionResult.workflowCompleted;
    results.nextTaskActivated = progressionResult.nextTask !== null;

    console.log('🎯 Final processTaskCompletion results:', results);

    return results;
  } catch (error) {
    console.error('Error processing task completion:', error);
    throw new Error(`Failed to process task completion: ${error.message}`);
  }
};

/**
 * Get task progression summary for an order
 * @param {Array} tasks - Array of task objects
 * @param {string} tareaOrdenId - The ID of the tarea_orden record
 * @returns {Promise<Object>} Progression summary
 */
export const getTaskProgressionSummary = (tasks, plan) => {
  if (!tasks || !Array.isArray(tasks) || !plan) {
    return {
      totalTasks: 0,
      completedTasks: 0,
      inProgressTasks: 0,
      pendingTasks: 0,
      progress: 0,
      allCompleted: false
    };
  }

  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(task => task.fin || task.fecha_fin).length;
  const inProgressTasks = tasks.filter(task => (task.inicio || task.fecha_inicio) && !(task.fin || task.fecha_fin)).length;
  const pendingTasks = tasks.filter(task => !(task.inicio || task.fecha_inicio) && !(task.fin || task.fecha_fin)).length;
  
  // Use synchronous calculation for summary
  let progress = 0;
  if (plan && typeof plan.peso === 'number') {
    const totalWeight = plan.peso;
    const completedWeight = tasks
      .filter(task => task.fin || task.fecha_fin)
      .reduce((sum, task) => sum + (task.peso || 0), 0);
    progress = Math.round((completedWeight / totalWeight) * 100);
  }
  
  const allCompleted = areAllTasksCompleted(tasks);

  return {
    totalTasks,
    completedTasks,
    inProgressTasks,
    pendingTasks,
    progress,
    allCompleted
  };
};
